import { clientsApi } from "#/api/clientsApi";
import { uploadApi } from "#/api/uploadApi";

import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import {
  SupportedAuthenticationMethod,
  type CreateClientPayload,
} from "#/types/client";
import { sanitize } from "#/utils/StringUtils";
import {
  Button,
  ColorInput,
  Drawer,
  Select,
  TextInput,
  type DrawerProps,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconImageInPicture } from "@tabler/icons-react";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

export interface Props extends DrawerProps {
  onSuccess: (clientId: number) => void;
}

const CreateClientDrawer = ({ onSuccess, ...props }: Props) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [saving, setSaving] = useState<boolean>(false);
  const [logoFile, setLogoFile] = useState<File>();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const form = useForm<CreateClientPayload>({
    initialValues: {
      name: "",
      slug: "",
      color: "",
      supportedAuthenticationMethods: SupportedAuthenticationMethod.PASSWORD,
      address: "",
      contactEmail: "",
    },
    validate: {
      name: (value) => (value ? null : t("clients.validation.nameRequired")),
      slug: (value) => (value ? null : t("clients.validation.slugRequired")),
      color: (value) => (value ? null : t("clients.validation.colorRequired")),
      address: (value) => (value ? null : t("clients.validation.addressRequired")),
      contactEmail: (value) => {
        if (!value) {
          return t("clients.validation.contactEmailRequired");
        }
        return /^\S+@\S+$/.test(value) ? null : t("clients.validation.invalidEmail");
      },
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const fileUrl = URL.createObjectURL(selectedFile);
      setPreviewUrl(fileUrl);
      setLogoFile(selectedFile);
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!logoFile) {
      showErrorNotification({
        message: t("clients.validation.pleaseSelectLogo"),
      });
      return;
    }
    setSaving(true);
    try {
      const logoUrl = await uploadApi.upload(logoFile);
      const response = await clientsApi.create({
        ...values,
        logo: logoUrl,
      });
      setSaving(false);
      showSuccessNotification({
        message: t("clients.createdSuccess"),
      });
      form.reset();
      setPreviewUrl(null);
      setLogoFile(undefined);
      onSuccess(response.data.id);
    } catch (error) {
      console.error("🚀 ~ handleSubmit ~ error:", error);
      setSaving(false);
    }
  };

  return (
    <Drawer
      {...props}
      position={"right"}
      title={t("clients.createNewClient")}
      closeOnClickOutside={false}
      onClose={() => {
        form.reset();
        props.onClose();
      }}
    >
      <form
        className="space-y-8 relative"
        onSubmit={form.onSubmit(handleSubmit)}
      >
        <div className="w-full grid grid-cols-1  gap-4">
          <TextInput
            label={t("clients.form.name")}
            withAsterisk
            placeholder={t("clients.form.namePlaceholder")}
            {...form.getInputProps("name")}
          />

          <TextInput
            label={t("clients.form.slug")}
            withAsterisk
            placeholder={t("clients.form.slugPlaceholder")}
            description={t("clients.form.slugDescription")}
            {...form.getInputProps("slug")}
          />

          <Select
            label={t("clients.form.authMethod")}
            placeholder={t("clients.form.authMethodPlaceholder")}
            searchable
            data={Object.values(SupportedAuthenticationMethod).map(
              (status) => ({
                label: sanitize(status),
                value: status,
              })
            )}
            value={form.values.supportedAuthenticationMethods}
            clearable
            onChange={(_value) => {
              form.setValues({
                ...form.values,
                supportedAuthenticationMethods:
                  _value as SupportedAuthenticationMethod,
              });
            }}
          />

          <TextInput
            label={t("clients.form.address")}
            withAsterisk
            placeholder={t("clients.form.addressPlaceholder")}
            {...form.getInputProps("address")}
          />

          <TextInput
            label={t("clients.form.contactEmail")}
            withAsterisk
            placeholder={t("clients.form.contactEmailPlaceholder")}
            {...form.getInputProps("contactEmail")}
          />

          <ColorInput
            label={t("clients.form.color")}
            withAsterisk
            placeholder={t("clients.form.colorPlaceholder")}
            {...form.getInputProps("color")}
          />

          <div className="w-full flex flex-col gap-2 relative">
            <span className="text-xs font-bold text-slate-600">{t("clients.form.logo")}</span>
            <div className="w-full grid grid-cols-[auto_1fr] items-center gap-4 text-gray-400">
              <div className="w-24 h-24 border  border-gray-200 rounded-lg flex items-center justify-center relative">
                {!previewUrl ? (
                  <IconImageInPicture size={40} />
                ) : (
                  <img
                    src={previewUrl}
                    alt="Logo"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="flex flex-col gap-4 w-full">
                <span>{t("clients.form.selectImage")}</span>
                <Button variant="outline">{t("clients.form.selectFile")}</Button>
              </div>
            </div>
            <input
              type="file"
              accept="image/jpeg, image/png"
              className="w-full h-full absolute top-0 left-0 z-10 opacity-0 cursor-pointer"
              onChange={handleFileSelect}
            />
          </div>
        </div>

        <div className="w-full grid grid-cols-2 gap-4 sticky bottom-0 left-0 bg-white border-t border-t-gray-200 py-6">
          <Button
            color="black"
            variant="outline"
            type="reset"
            onClick={() => {
              form.reset();
            }}
            size="md"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={saving}
              type="submit"
              size="md"
              disabled={!canWrite()}
              onClick={() => {
                void handleSubmit(form.values);
              }}
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      </form>
    </Drawer>
  );
};

export default CreateClientDrawer;
